import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Field, StatusBadge } from "@/components/app/common";
import { AuditPanel } from "@/components/app/AuditPanel";
import { DocumentsPanel } from "@/components/app/DocumentsPanel";
import { useMe } from "@/components/app/MeContext";
import { AddressCard } from "@/components/order/AddressCard";
import { DcActions } from "@/components/logistics/DcActions";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate, label, today } from "@/lib/format";

const DELIVERY = ["in_transit", "delivered", "partially_delivered", "rejected", "returned"];
const POD = ["pending", "received", "not_required"];

const Pick = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{label(o)}</SelectItem>)}</SelectContent></Select>
);

function DeliveryForm({ d, onDone }) {
  const [f, setF] = useState({ delivery_status: d.delivery_status, delivered_date: d.delivered_date || "", pod_status: d.pod_status || "pending", pod_date: d.pod_date || "", note: "" });
  const save = async () => {
    try { await api.post(`/dispatches/${d.id}/delivery`, f); toast.success("Dispatch updated"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const done = ["delivered", "partially_delivered"].includes(f.delivery_status);
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="dispatch-delivery-form">
      <div><Label>Delivery</Label><Pick value={f.delivery_status} onChange={(v) => setF({ ...f, delivery_status: v, delivered_date: f.delivered_date || today() })} options={DELIVERY} testid="dsp-delivery-status" /></div>
      <div><Label>Delivered on</Label><Input type="date" disabled={!done} value={f.delivered_date} onChange={(e) => setF({ ...f, delivered_date: e.target.value })} data-testid="dsp-delivered-date" /></div>
      <div><Label>POD</Label><Pick value={f.pod_status} onChange={(v) => setF({ ...f, pod_status: v })} options={POD} testid="dsp-pod-status" /></div>
      <div><Label>POD date</Label><Input type="date" disabled={f.pod_status !== "received"} value={f.pod_date} onChange={(e) => setF({ ...f, pod_date: e.target.value })} data-testid="dsp-pod-date" /></div>
      <div className="col-span-2"><Label>Note</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="dsp-note" /></div>
      <div className="col-span-2 flex justify-end"><Button size="sm" onClick={save} data-testid="dsp-update-btn">Save changes</Button></div>
    </div>
  );
}

function InvoiceLink({ d, onDone }) {
  const { data } = useFetch(`/invoices?order_id=${d.order_id}&status=active`);
  const NONE = "__none";
  const link = async (v) => {
    try { await api.post(`/dispatches/${d.id}/invoice`, { invoice_id: v === NONE ? null : v }); toast.success("Invoice link updated"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 max-w-md"><Label>Linked invoice (Invoice Register)</Label>
      <Select value={d.invoice_id || NONE} onValueChange={link}><SelectTrigger data-testid="dsp-invoice-link"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value={NONE}>Not linked</SelectItem>{(data || []).map((x) => <SelectItem key={x.id} value={x.id}>{x.invoice_no} · {x.dispatch_address_snapshot?.address_label}</SelectItem>)}</SelectContent></Select></div>
  );
}

export function DispatchSheet({ dispatch, onClose, onDone }) {
  const { can } = useMe();
  const [tick, setTick] = useState(0);
  useEffect(() => { setTick(0); }, [dispatch?.id]);
  if (!dispatch) return null;
  const d = dispatch;
  const cancelled = d.dispatch_status === "cancelled";
  const cancel = async () => {
    const reason = window.prompt(`Reason for cancelling ${d.dispatch_no}? Quantities go back to the LM.`);
    if (!reason) return;
    try { await api.post(`/dispatches/${d.id}/cancel`, { reason }); toast.success("Dispatch cancelled"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="dispatch-detail-sheet">
        <SheetHeader><SheetTitle className="flex flex-wrap items-center gap-2"><span className="font-mono">{d.dispatch_no}</span>{cancelled ? <StatusBadge status="cancelled" /> : <><StatusBadge status={d.delivery_status} /><StatusBadge status={`pod_${d.pod_status}`} /></>}</SheetTitle></SheetHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <Field k="LM" v={<Link className="text-sky-700 font-mono" to={`/orders/${d.order_id}`}>{d.order_no}</Link>} />
          <Field k="Movement" v={d.movement_no} mono /><Field k="Customer" v={d.customer_name} />
          <Field k={d.document_type === "delivery_challan" ? "DC no." : "Invoice no."} v={d.document_type === "delivery_challan" ? d.dc_no : d.invoice_no} mono />
          <Field k="Dispatch date" v={fmtDate(d.dispatch_date)} /><Field k="Transporter" v={d.transporter?.name} />
          <Field k="LR / docket" v={[d.lr_no, fmtDate(d.lr_date)].filter(Boolean).join(" · ")} mono /><Field k="Vehicle" v={d.vehicle_no} mono />
          {!d.dispatch_address_snapshot && <Field k="Destination" v={d.destination} />}<Field k="Expected delivery" v={fmtDate(d.expected_delivery_date)} />
          <Field k="Driver" v={[d.driver_name, d.driver_phone].filter(Boolean).join(" · ")} />
          {cancelled && <Field k="Cancel reason" v={d.cancel_reason} />}
        </div>
        {d.dc_no && <div className="mt-4"><DcActions base={`/dispatches/${d.id}`} dcNo={d.dc_no} doc={d} canEmail={!cancelled && can("write")} /></div>}
        <h3 className="text-sm font-semibold mt-4 mb-2">Deliver to</h3>
        {d.dispatch_address_snapshot && <AddressCard a={d.dispatch_address_snapshot} testid="dispatch-address-card" />}
        {!cancelled && can("write") && d.document_type !== "delivery_challan" && <InvoiceLink d={d} onDone={onDone} />}
        <div className="mt-3 text-xs text-slate-700" data-testid="dispatch-items">{d.items.map((i) => `L${i.line_no} ${i.description}: ${i.qty} ${i.uom}`).join(" · ")}</div>
        {!cancelled && can("write") && <div className="mt-5 rounded border border-slate-200 p-3"><h3 className="text-sm font-semibold mb-2">Delivery & POD</h3><DeliveryForm key={d.id} d={d} onDone={onDone} />
          <p className="text-[11px] text-slate-500 mt-2">POD can be marked received only after a POD document is uploaded below.</p></div>}
        <h3 className="text-sm font-semibold mt-5 mb-2">LR, POD, Eway Bill & transporter invoice</h3>
        <DocumentsPanel entityType="dispatch" entityId={d.id} onChange={() => setTick((t) => t + 1)} />
        {!cancelled && can("write") && d.delivery_status === "in_transit" && <Button size="sm" variant="outline" className="mt-4 text-red-600" onClick={cancel} data-testid="dsp-cancel-btn">Cancel dispatch</Button>}
        <h3 className="text-sm font-semibold mt-5 mb-2">History</h3>
        <AuditPanel entityId={d.id} refreshKey={tick} />
      </SheetContent>
    </Sheet>
  );
}
